import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import type { DomainSummary } from "@/features/data-hub/model/domainsSlice";

type ChatInputBarProps = {
  domains: DomainSummary[];
  selectedDomainId: number | "";
  onDomainChange: (domainId: number) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStopClick?: () => void;
  isProcessing: boolean;
  isStopping?: boolean;
  domainLocked?: boolean;
};

export function ChatInputBar({
  domains,
  selectedDomainId,
  onDomainChange,
  inputValue,
  onInputChange,
  onSend,
  onStopClick,
  isProcessing,
  isStopping = false,
  domainLocked = false,
}: ChatInputBarProps) {
  const { t } = useTranslation("chat");
  const showSend = inputValue.trim().length > 0;
  const selectedDomain = domains.find((domain) => domain.id === selectedDomainId);

  const handleDomainChange = (event: SelectChangeEvent<number | "">) => {
    const value = Number(event.target.value);
    if (Number.isFinite(value)) {
      onDomainChange(value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (showSend && !isProcessing) {
        onSend();
      }
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800,
        mx: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 999,
          border: "1px solid color-mix(in srgb, var(--theme-primary) 35%, var(--theme-border))",
          bgcolor: "var(--theme-surface)",
          px: 1.5,
          py: 0.75,
          minHeight: 52,
        }}
      >
        {domainLocked ? (
          <Typography
            variant="body2"
            noWrap
            sx={{
              flexShrink: 0,
              maxWidth: 180,
              color: "var(--theme-text-primary)",
              fontSize: "0.875rem",
              py: 0.5,
            }}
          >
            {selectedDomain?.name ?? "—"}
          </Typography>
        ) : (
          <Select
            value={selectedDomainId}
            onChange={handleDomainChange}
            displayEmpty
            variant="standard"
            disableUnderline
            IconComponent={KeyboardArrowDownIcon}
            aria-label={t("domainSelectAria")}
            sx={{
              flexShrink: 0,
              maxWidth: 180,
              color: "var(--theme-text-primary)",
              fontSize: "0.875rem",
              "& .MuiSelect-select": {
                py: 0.5,
                pr: "28px !important",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiSelect-icon": {
                color: "var(--theme-text-secondary)",
                right: 0,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: "var(--theme-surface)",
                  border: "1px solid var(--theme-border)",
                },
              },
            }}
          >
            {domains.length === 0 ? (
              <MenuItem value="" disabled>
                —
              </MenuItem>
            ) : (
              domains.map((domain) => (
                <MenuItem key={domain.id} value={domain.id}>
                  {domain.name}
                </MenuItem>
              ))
            )}
          </Select>
        )}

        <Box
          sx={{
            width: "1px",
            alignSelf: "stretch",
            my: 0.75,
            bgcolor: "var(--theme-border)",
            flexShrink: 0,
          }}
        />

        <InputBase
          value={inputValue}
          onChange={(event) => {
            onInputChange(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={t("inputPlaceholder")}
          disabled={isProcessing}
          multiline
          maxRows={4}
          sx={{
            flex: 1,
            minWidth: 0,
            color: "var(--theme-text-primary)",
            fontSize: "0.9375rem",
            "& .MuiInputBase-input": {
              py: 0.75,
              "&::placeholder": {
                color: "var(--theme-text-secondary)",
                opacity: 1,
              },
            },
          }}
        />

        {isProcessing ? (
          <IconButton
            onClick={onStopClick}
            disabled={isStopping || !onStopClick}
            aria-label={t("stopButton")}
            title={t("stopButton")}
            size="small"
            sx={{
              flexShrink: 0,
              width: 32,
              height: 32,
              bgcolor: "var(--theme-error, #d32f2f)",
              color: "var(--theme-text-on-primary, #fff)",
              "&:hover": {
                bgcolor: "color-mix(in srgb, var(--theme-error, #d32f2f) 85%, black)",
              },
              "&.Mui-disabled": {
                bgcolor: "var(--theme-overlay-olive-15)",
                color: "var(--theme-text-disabled)",
              },
            }}
          >
            <StopCircleOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : showSend ? (
          <IconButton
            onClick={onSend}
            disabled={isProcessing}
            aria-label={t("sendAria")}
            size="small"
            sx={{
              flexShrink: 0,
              width: 32,
              height: 32,
              bgcolor: "var(--theme-primary)",
              color: "var(--theme-text-on-primary)",
              "&:hover": {
                bgcolor: "var(--theme-hover-primary, var(--theme-primary-light))",
              },
              "&.Mui-disabled": {
                bgcolor: "var(--theme-overlay-olive-15)",
                color: "var(--theme-text-disabled)",
              },
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
}
